import { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link,
  Toolbar,
  Typography,
  Breadcrumbs,
  Chip,
  Collapse,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  FaBars,
  FaHome,
  FaChevronDown,
  FaUserCircle,
  FaKey,
  FaSignOutAlt,
  FaEnvelope,
  FaCrown,
} from 'react-icons/fa';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
// @ts-expect-error - Font import
import '@fontsource/righteous';
import { useAuth, useIsSuperAdmin } from '@/hooks';
import { useLoading } from '@/hooks/useLoading';
import { authApi } from '@/features/auth';
import FullScreenLoading from '@/components/common/FullLoading';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import ContactDialog from '@/components/common/ContactDialog';
import { getMenuItems, getRouteByPath, routeConfig } from '@/config/routes';
import { useAppStore } from '@/stores';
import ChangePasswordDialog from '@/components/common/ChangePasswordDialog';
import { TourController } from '@/components/common';
import { PUBLIC_ROUTES, PROTECTED_ROUTES, isSubscriptionAllowed } from '@/constants/routes';
import { useSubscriptionMe } from '@/features/subscription';

const drawerWidth = 230;
const collapsedDrawerWidth = 70;

// Menu title translation key mapping
const MENU_TITLE_KEYS: Record<string, string> = {
  '/': 'menu.landing',
  '/ai-chat': 'menu.chat',
  '/social-analysis': 'menu.socialAnalysis',
  '/ai-publish': 'menu.aiPublish',
  '/usage': 'menu.usage',
  '/orders': 'menu.orders',
  '/settings': 'menu.settings',
  '/orders/list': 'menu.orderList',
  '/orders/import': 'menu.importOrders',
  '/orders/export': 'menu.exportOrders',
  '/settings/company': 'menu.companySettings',
  '/settings/brand': 'menu.brandSettings',
  '/settings/number': 'menu.numberSettings',
  '/settings/logistics': 'menu.logisticsSettings',
  '/settings/payment': 'menu.paymentSettings',
  // Admin routes
  '/admin': 'menu.admin',
  '/admin/users': 'menu.adminUsers',
  '/admin/settings': 'menu.adminSettings',
  '/admin/health': 'menu.adminHealth',
};

// Routes that should hide the sidebar
const getShowSidebar = (pathname: string): boolean => {
  const route = routeConfig.find((r) => r.path === pathname);
  return route?.showSidebar !== false;
};

// Routes that should hide the header completely
const getShowHeader = (pathname: string): boolean => {
  const route = routeConfig.find((r) => r.path === pathname);
  return route?.showHeader !== false;
};

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useLoading();
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const themeMode = useAppStore((state) => state.ui.themeMode);
  const isDark = themeMode === 'dark';

  // Auto-hide header on scroll down, show on scroll up
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let lastDirection: 'up' | 'down' | null = null;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Always show at top
      if (currentScrollY < 10) {
        setIsHeaderVisible(true);
        lastScrollY = currentScrollY;
        lastDirection = null;
        return;
      }

      const direction = currentScrollY > lastScrollY ? 'down' : 'up';

      // Only update when direction changes or crosses threshold
      if (direction === 'down' && currentScrollY > lastScrollY + 5) {
        // Hide immediately when scrolling down 5px
        setIsHeaderVisible(false);
        lastScrollY = currentScrollY;
        lastDirection = 'down';
      } else if (direction === 'up' && lastDirection === 'down') {
        // Show when direction changes from down to up
        setIsHeaderVisible(true);
        lastScrollY = currentScrollY;
        lastDirection = 'up';
      } else if (direction === 'up') {
        // Keep updating lastScrollY while scrolling up
        lastScrollY = currentScrollY;
        lastDirection = 'up';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-close mobile drawer when resizing to desktop
  useEffect(() => {
    if (!isMobile) setMobileDrawerOpen(false);
  }, [isMobile]);

  // Check if sidebar should be shown for current route
  const showSidebar = getShowSidebar(location.pathname);
  // Check if header should be shown for current route
  const showHeader = getShowHeader(location.pathname);

  // Use complete auth hook (includes auth check and state)
  const { user, isUserAuthenticated, logout, setUser } = useAuth();
  const { data: subscriptionMe } = useSubscriptionMe({ enabled: isUserAuthenticated });
  const isSuperAdmin = useIsSuperAdmin();

  const membershipBadge = ((): {
    label: string;
    color: 'success' | 'warning' | 'default' | 'info';
  } | null => {
    if (isSuperAdmin) {
      return { label: t('membership.admin'), color: 'info' };
    }
    if (!subscriptionMe) return null;
    if (subscriptionMe.plan === 'paid') {
      return { label: t('membership.pro'), color: 'success' };
    }
    if (subscriptionMe.accessState === 'trial' && subscriptionMe.trialEndsAt) {
      const daysLeft = Math.max(
        0,
        Math.ceil((new Date(subscriptionMe.trialEndsAt).getTime() - Date.now()) / 86_400_000)
      );
      return { label: t('membership.trialRemaining', { days: daysLeft }), color: 'warning' };
    }
    if (subscriptionMe.accessState === 'expired') {
      return { label: t('membership.expired'), color: 'default' };
    }
    return { label: t('membership.free'), color: 'default' };
  })();
  const menuItems = getMenuItems(user?.role, isSuperAdmin);

  // Fetch user profile if authenticated but userName is empty
  useEffect(() => {
    const fetchProfileIfNeeded = async () => {
      if (isUserAuthenticated && !user?.userName) {
        try {
          const userProfile = await authApi.getProfile();
          setUser(userProfile);
        } catch (error) {
          console.error('❌ Failed to fetch user profile:', error);
        }
      }
    };
    fetchProfileIfNeeded();
  }, [isUserAuthenticated, user?.userName, setUser]);

  // Get translated title for menu items
  const getMenuTitle = (path: string, defaultTitle: string): string => {
    const key = MENU_TITLE_KEYS[path];
    if (key) {
      return t(key);
    }
    return defaultTitle;
  };

  // Toggle submenu expand/collapse
  const handleMenuToggle = (menuPath: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedMenus((prev) => ({
      ...prev,
      [menuPath]: !prev[menuPath],
    }));
  };

  // Generate breadcrumbs
  const generateBreadcrumbs = () => {
    const homePath = '/';

    // If on home page, return empty (no breadcrumbs needed)
    if (location.pathname === '/') {
      return [];
    }

    // Use route config to generate breadcrumbs
    const currentRoute = getRouteByPath(location.pathname);
    const breadcrumbs = [{ label: t('common.home'), path: homePath }];

    if (currentRoute) {
      breadcrumbs.push({
        label: getMenuTitle(currentRoute.path, currentRoute.title),
        path: currentRoute.path,
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileDrawerOpen(false);
  };

  const handleSidebarToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // User menu handlers
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleChangePassword = () => {
    handleUserMenuClose();
    setChangePasswordOpen(true);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleManageSubscription = () => {
    handleUserMenuClose();
    navigate(PROTECTED_ROUTES.SUBSCRIPTION);
  };

  const drawer = (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        onClick={handleSidebarToggle}
        className={clsx('toggle-button', {
          'toggle-button--collapsed': isCollapsed,
          'toggle-button--expanded': !isCollapsed,
        })}
        sx={{ display: { xs: 'none', md: 'block' } }}
      >
        <FaBars />
      </IconButton>

      <Toolbar
        className={clsx('brand-toolbar', isDark ? 'brand-toolbar--dark' : 'brand-toolbar--light')}
        sx={{ justifyContent: 'flex-start', px: 2 }}
      >
        {(!isCollapsed || isMobile) && (
          <Box
            component="img"
            src={isDark ? '/intellio-wordmark-dark.svg' : '/intellio-wordmark-light.svg'}
            alt="Intellio AI"
            sx={{
              height: 42,
              width: 'auto',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/')}
          />
        )}
      </Toolbar>
      <List sx={{ pt: 2 }}>
        {menuItems.map((item) => (
          <Box key={item.path}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={(e) => {
                  if (item.expandable) {
                    handleMenuToggle(item.path, e);
                  } else {
                    handleMenuClick(item.path);
                  }
                }}
                className={clsx('menu-list-item-button', {
                  'menu-list-item-button--collapsed': isCollapsed,
                  'menu-list-item-button--light': !isDark,
                  'menu-list-item-button--dark': isDark,
                })}
              >
                <ListItemIcon
                  sx={{
                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#000',
                    minWidth: isCollapsed ? 'auto' : 28,
                    mr: isCollapsed ? 0 : 0.5,
                    fontSize: 20,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <>
                    <ListItemText
                      primary={getMenuTitle(item.path, item.title)}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontSize: 15,
                          fontWeight: 800,
                          lineHeight: 1.6,
                          color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#666',
                        },
                      }}
                    />
                    {item.expandable && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          ml: 'auto',
                          transform: expandedMenus[item.path] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      >
                        <FaChevronDown
                          style={{
                            fontSize: 14,
                            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </ListItemButton>
            </ListItem>
            {item.expandable && item.children && (
              <Collapse in={expandedMenus[item.path]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem key={child.path} disablePadding sx={{ pl: isCollapsed ? 0 : 2 }}>
                      <ListItemButton
                        onClick={() => handleMenuClick(child.path)}
                        className={clsx('menu-list-item-button', {
                          'menu-list-item-button--collapsed': isCollapsed,
                          'menu-list-item-button--light': !isDark,
                          'menu-list-item-button--dark': isDark,
                        })}
                        sx={{
                          '&.MuiButtonBase-root': {
                            paddingLeft: isCollapsed ? 0 : '16px',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#000',
                            minWidth: isCollapsed ? 'auto' : 28,
                            mr: isCollapsed ? 0 : 0.5,
                            fontSize: 14,
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        {!isCollapsed && (
                          <ListItemText
                            primary={getMenuTitle(child.path, child.title)}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontSize: 14,
                                fontWeight: 800,
                                lineHeight: 1.5,
                                color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#777',
                              },
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>
    </Box>
  );

  const currentDrawerWidth =
    showSidebar && !isMobile ? (isCollapsed ? collapsedDrawerWidth : drawerWidth) : 0;

  return (
    <Box
      className={clsx(
        'layout-container',
        isDark ? 'layout-container--dark' : 'layout-container--light'
      )}
    >
      <CssBaseline />
      <FullScreenLoading open={loading} />
      {showHeader && (
        <AppBar
          position="fixed"
          className={clsx('main-app-bar', isDark ? 'main-app-bar--dark' : 'main-app-bar--light')}
          color="transparent"
          elevation={0}
          sx={{
            width: showSidebar ? `calc(100% - ${currentDrawerWidth}px)` : '100%',
            marginLeft: showSidebar ? `${currentDrawerWidth}px` : 0,
            backgroundImage: 'none',
            transform: isHeaderVisible ? 'translateY(0)' : 'translateY(-100%)',
            visibility: isHeaderVisible ? 'visible' : 'hidden',
            pointerEvents: isHeaderVisible ? 'auto' : 'none',
            transition: isHeaderVisible
              ? 'transform 0.2s ease-out, visibility 0s'
              : 'transform 0.15s ease-in, visibility 0s 0.15s',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 64, sm: 64 } }}>
            {/* Hamburger menu for mobile */}
            {showSidebar && (
              <IconButton
                aria-label="open navigation menu"
                aria-expanded={mobileDrawerOpen}
                aria-controls="mobile-nav-drawer"
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}
              >
                <FaBars />
              </IconButton>
            )}
            {/* Brand logo when no sidebar */}
            {!showSidebar && (
              <Box
                component="img"
                src={isDark ? '/intellio-wordmark-dark.svg' : '/intellio-wordmark-light.svg'}
                alt="Intellio AI"
                sx={{
                  height: 42,
                  width: 'auto',
                  cursor: 'pointer',
                  mr: { xs: 2, sm: 3, md: 4 },
                }}
                onClick={() => navigate('/')}
              />
            )}
            {/* 麵包屑導覽 */}
            <Breadcrumbs
              aria-label="breadcrumb"
              separator="/"
              sx={{ color: 'text.secondary', fontSize: 14, display: { xs: 'none', md: 'flex' } }}
            >
              {breadcrumbs.map((crumb, index) =>
                index === breadcrumbs.length - 1 ? (
                  <Typography
                    key={crumb.path}
                    color="text.primary"
                    sx={{ fontSize: 14, fontWeight: 600 }}
                  >
                    {crumb.label}
                  </Typography>
                ) : (
                  <Link
                    key={crumb.path}
                    color="inherit"
                    href="#"
                    underline="none"
                    className="breadcrumb-link"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(crumb.path);
                    }}
                  >
                    {index === 0 && <FaHome style={{ marginRight: 4, fontSize: 12 }} />}
                    {crumb.label}
                  </Link>
                )
              )}
            </Breadcrumbs>

            <Box sx={{ flexGrow: { xs: 0, md: 1 } }} />
            <Box className="user-section" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isUserAuthenticated ? (
                <>
                  {membershipBadge && (
                    <Chip
                      label={membershipBadge.label}
                      color={membershipBadge.color}
                      size="small"
                      variant="outlined"
                      onClick={
                        isSubscriptionAllowed()
                          ? () => navigate(PROTECTED_ROUTES.SUBSCRIPTION)
                          : undefined
                      }
                      sx={{
                        cursor: isSubscriptionAllowed() ? 'pointer' : 'default',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {/* User dropdown menu */}
                  <Link
                    onClick={handleUserMenuOpen}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      borderRadius: '20px',
                      px: 2,
                      py: 0.5,
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.15)'
                            : 'rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <FaUserCircle style={{ fontSize: 18 }} />
                    <Typography variant="body2" color="inherit">
                      {user?.userName}
                    </Typography>
                    <FaChevronDown style={{ fontSize: 12 }} />
                  </Link>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          minWidth: 180,
                        },
                      },
                    }}
                  >
                    <MenuItem onClick={handleChangePassword}>
                      <FaKey style={{ marginRight: 12, fontSize: 14 }} />
                      {t('common.changePassword')}
                    </MenuItem>
                    {isSubscriptionAllowed() && (
                      <MenuItem onClick={handleManageSubscription}>
                        <FaCrown style={{ marginRight: 12, fontSize: 14 }} />
                        {t('common.subscription')}
                      </MenuItem>
                    )}
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <FaSignOutAlt style={{ marginRight: 12, fontSize: 14 }} />
                      {t('common.logout')}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                /* Login button for guests */
                <Link
                  onClick={() => navigate(PUBLIC_ROUTES.LOGIN)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderRadius: '20px',
                    px: 2,
                    py: 0.5,
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  <FaUserCircle style={{ fontSize: 18 }} />
                  <Typography variant="body2" color="inherit">
                    {t('common.login')}
                  </Typography>
                </Link>
              )}
              <LanguageSwitcher />
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  }}
                />
                <IconButton
                  data-tour="contact-email"
                  onClick={() => setContactDialogOpen(true)}
                  size="small"
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  <FaEnvelope />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      {/* Contact Dialog */}
      <ContactDialog open={contactDialogOpen} onClose={() => setContactDialogOpen(false)} />

      <TourController />
      {showSidebar && (
        <Box component="nav" sx={{ width: currentDrawerWidth, flexShrink: 0 }}>
          <Drawer
            id="mobile-nav-drawer"
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? mobileDrawerOpen : true}
            onClose={() => setMobileDrawerOpen(false)}
            className={clsx('main-drawer', isDark ? 'main-drawer--dark' : 'main-drawer--light')}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: isMobile ? drawerWidth : currentDrawerWidth,
              },
            }}
          >
            {drawer}
          </Drawer>
        </Box>
      )}
      <Box
        className="main-content"
        sx={{
          width: showSidebar ? `calc(100% - ${currentDrawerWidth}px)` : '100%',
        }}
      >
        {showHeader && <Toolbar sx={{ minHeight: { xs: 64, sm: 64 } }} />}
        <Outlet />
      </Box>
    </Box>
  );
}
